import { useEffect, useMemo, useState, Fragment, type ReactNode } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Users, MapPin, HardDrive, Calendar, Globe, Palette, ToggleLeft, BarChart3, ChevronRight, ChevronDown, CreditCard, Settings, Youtube, RefreshCw, Lock, UserCheck, ShieldAlert, KeyRound, Loader2, LayoutTemplate, Shield, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { usePlatformStore } from '@/lib/store';
import { useAuth } from '@/contexts/AuthContext';
import { StatusBadge, PlanBadge, Spinner, Tabs, Toggle } from '@/components/shared/UIComponents';
import { formatDate, formatStorage, formatCompact } from '@/utils/helpers';
import { FEATURE_META, APP_B2C_ONLY_KEYS, APP_FEATURE_KEYS, PLATFORM_FEATURE_KEYS } from '@/lib/constants';
import { fetchUsageMetrics, fetchTenantUsers, provisionTenantInAppDb, fetchPlanFeatureDefaults, fetchTenantAdminSigninActivity, startImpersonation, setUserRight, setCreatorTier } from '@/lib/api';
import type { AppTenantUser, TenantAdminSigninActivity } from '@/lib/api';
import { guestDisplayName, shortId } from '@/lib/guestName';
import { TenantPlanHistory } from '@/components/tenants/TenantPlanHistory';
import { UserPlanHistory } from '@/components/tenants/UserPlanHistory';
import type { TenantPlan, BrandingConfig, FeatureConfig, UsageMetric, PlanFeatureDefault } from '@/types';

// ── Window selector helpers ─────────────────────────────────────────
type Preset = 7 | 14 | 30 | 90;
type WindowSel =
  | { kind: 'preset'; days: Preset }
  | { kind: 'custom'; from: string; to: string };

function isoDate(d: Date): string { return d.toISOString().split('T')[0]; }
function addDays(d: Date, n: number): Date { return new Date(d.getTime() + n * 86400000); }

function resolveWindow(sel: WindowSel): { from: string; to: string; days: number; label: string } {
  if (sel.kind === 'preset') {
    const today = new Date();
    return {
      from: isoDate(addDays(today, -(sel.days - 1))),
      to:   isoDate(today),
      days: sel.days,
      label: `Last ${sel.days} days`,
    };
  }
  const days = Math.max(1, Math.round((+new Date(sel.to) - +new Date(sel.from)) / 86400000) + 1);
  return { from: sel.from, to: sel.to, days, label: `${sel.from} → ${sel.to}` };
}

export function TenantDetailPage() {
  const { tenantId } = useParams();
  const { selectedTenant: tenant, loading, selectTenant, clearSelectedTenant, updateTenant, updateFeatures } = usePlatformStore();
  const { isSuperAdmin } = useAuth();
  const location = useLocation();
  // Honour a #<tab> deep-link (e.g. the Cost Tracking → tenant link uses
  // #costs) so clicking a tenant lands straight on that tab instead of
  // defaulting to Overview.
  const [tab, setTab] = useState(() => location.hash.replace(/^#/, '') || 'overview');
  useEffect(() => {
    const h = location.hash.replace(/^#/, '');
    if (h) setTab(h);
  }, [location.hash]);
  const [metrics, setMetrics] = useState<UsageMetric[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [appUsers, setAppUsers] = useState<AppTenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // Per-plan defaults for the tenant's current plan — drives the App
  // Features column on the Features tab (so it reflects "ABC Travel
  // is on Pro and gets X / Y / Z limits") instead of a generic list.
  const [planDefaults, setPlanDefaults] = useState<PlanFeatureDefault[]>([]);

  // Window selector — drives the metrics fetch for both Overview and
  // Usage tabs. Default = last 14 days (matches the prior behavior).
  const [window, setWindowSel] = useState<WindowSel>({ kind: 'preset', days: 14 });
  const range = useMemo(() => resolveWindow(window), [window]);

  useEffect(() => {
    if (tenantId) selectTenant(tenantId);
    return () => clearSelectedTenant();
  }, [tenantId, selectTenant, clearSelectedTenant]);

  // Refetch metrics whenever the tenant or the resolved window
  // changes. setMetricsLoading toggles the "Updating…" pill in the
  // window selector + the subtle opacity dim on the panels below
  // so the refresh is visibly happening (no full-page reload).
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setMetricsLoading(true);
    fetchUsageMetrics(tenantId, { from: range.from, to: range.to })
      .then(data => { if (!cancelled) setMetrics(data); })
      .catch(() => { if (!cancelled) setMetrics([]); })
      .finally(() => { if (!cancelled) setMetricsLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, range.from, range.to]);

  // Cost drill-down — daily series + summary rollup for the same window.
  // Shipping with the existing window picker so super-admins can scrub
  // the date range across the same control they use for usage.
  //
  // We also load a 12-month series (independent of the day picker) so
  // the year total + monthly breakdown stays stable while admins scrub
  // the daily window for short-term diagnostics.

  // Load plan defaults whenever the tenant's plan changes. The Features
  // tab uses these to render the App Features column with the actual
  // is_enabled / limit_value for the tenant's current plan.
  useEffect(() => {
    const planId = (tenant?.plan as TenantPlan | undefined)?.id ?? tenant?.plan_id;
    if (!planId) { setPlanDefaults([]); return; }
    fetchPlanFeatureDefaults(planId).then(setPlanDefaults).catch(() => setPlanDefaults([]));
  }, [tenant?.plan_id, (tenant?.plan as TenantPlan | undefined)?.id]);

  // Fetch app users on tab='users' OR on first load so the Overview's
  // "Latest Users" panel has real data without waiting for a tab click.
  useEffect(() => {
    if (!tenantId) return;
    if (tab !== 'users' && appUsers.length > 0) return;
    setUsersLoading(true);
    fetchTenantUsers(tenantId)
      .then(setAppUsers)
      .catch(() => setAppUsers([]))
      .finally(() => setUsersLoading(false));
  }, [tenantId, tab, appUsers.length]);

  if (loading || !tenant) {
    return <div className="flex items-center justify-center py-32"><Spinner size="lg" /></div>;
  }

  const plan = tenant.plan as TenantPlan | undefined;
  const branding = tenant.branding as BrandingConfig | undefined;
  const features = tenant.features as FeatureConfig | undefined;
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    // Sign-in activity for the people on `tenant_admins`. Separate
    // tab from Users (which is App-side `tenant_users`) so the
    // super-admin can distinguish "did the agency-owner Bob ever log
    // in to manage his tenant?" from "how many end-user travelers
    // signed up this week?".
    { id: 'admins', label: 'Admins' },
    { id: 'branding', label: 'Branding' },
    { id: 'features', label: 'Features' },
    { id: 'usage', label: 'Usage' },
  ];

  return (
    <div className="space-y-6">
      {/* Back link + header */}
      <Link to="/admin/tenants" className="inline-flex items-center gap-2 text-sm text-surface-500 hover:text-surface-900 transition-colors">
        <ArrowLeft size={14} /> All Tenants
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shrink-0 shadow-md ring-2 ring-slate-500/30">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-900">{tenant.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <PlanBadge plan={plan?.name || 'free'} label={plan?.display_name} />
              <StatusBadge status={tenant.status} />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/tenants/${tenant.id}/manage`} className="btn-primary text-sm">
            Manage Tenant
          </Link>
          <a href={`https://${tenant.subdomain}`} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm">
            <ExternalLink size={14} /> Visit App
          </a>
          {tenant.status === 'active' ? (
            <button onClick={() => updateTenant(tenant.id, { status: 'suspended' })} className="btn-danger text-sm">Suspend</button>
          ) : (
            <button onClick={() => updateTenant(tenant.id, { status: 'active' })} className="btn-primary text-sm">Activate</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {/* Window selector — visible on tabs that consume metrics
          (Overview + Usage). Stays inline so the user can pivot
          between presets and custom range without leaving the page.
          Shows a live "Updating…" pill while metrics refetch. */}
      {(tab === 'overview' || tab === 'usage') && (
        <WindowSelector value={window} onChange={setWindowSel} rangeLabel={range.label} loading={metricsLoading} />
      )}

      {/* Tab content. We dim Overview's metric-driven panels + the
          Usage tab while refetching so the user sees the change is
          in flight without a full-page reload. */}
      <div className={metricsLoading && (tab === 'overview' || tab === 'usage') ? 'opacity-60 transition-opacity duration-200' : 'transition-opacity duration-200'}>
        {tab === 'overview' && <OverviewTab tenant={tenant} plan={plan} branding={branding} metrics={metrics} users={appUsers} rangeLabel={range.label} />}
        {tab === 'usage' && <UsageTab metrics={metrics} rangeLabel={range.label} rangeDays={range.days} planDefaults={planDefaults} />}
      </div>
      {tab === 'users' && <UsersTab tenantId={tenant.id} users={appUsers} loading={usersLoading} />}
      {tab === 'admins' && <AdminsTab tenantId={tenant.id} />}
      {tab === 'branding' && <BrandingTab branding={branding} />}
      {tab === 'features' && <FeaturesTab features={features} tenantId={tenant.id} updateFeatures={updateFeatures} plan={plan} planDefaults={planDefaults} isSuperAdmin={isSuperAdmin} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* OVERVIEW TAB                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function OverviewTab({ tenant, plan, branding, metrics, users, rangeLabel }: {
  tenant: any; plan?: TenantPlan; branding?: BrandingConfig; metrics: UsageMetric[]; users: AppTenantUser[]; rangeLabel: string;
}) {
  const totalTrips = metrics.reduce((s, m) => s + m.trips_created, 0);
  const totalAI = metrics.reduce((s, m) => s + m.ai_requests, 0);
  const avgUsers = metrics.length ? Math.round(metrics.reduce((s, m) => s + m.active_users, 0) / metrics.length) : 0;
  const navigate = useNavigate();
  const { updateTenant } = usePlatformStore();
  const base = `/admin/tenants/${tenant.id}/manage`;

  const [editingYtLimit, setEditingYtLimit] = useState(false);
  const [ytLimitValue, setYtLimitValue] = useState(String(tenant.max_youtube_imports ?? 20));
  const [savingYtLimit, setSavingYtLimit] = useState(false);

  // Re-provision B2C plans
  const [provisioning, setProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState<{ ok: boolean; message: string } | null>(null);
  const handleReprovision = async () => {
    setProvisioning(true);
    setProvisionResult(null);
    try {
      const res = await provisionTenantInAppDb(tenant.id, tenant.name);
      setProvisionResult({ ok: true, message: res.message || 'B2C plans provisioned successfully' });
    } catch (err: any) {
      setProvisionResult({ ok: false, message: err.message || 'Provisioning failed' });
    } finally {
      setProvisioning(false);
    }
  };

  const saveYtLimit = async () => {
    const val = parseInt(ytLimitValue);
    if (isNaN(val) || val < 0) return;
    setSavingYtLimit(true);
    try {
      await updateTenant(tenant.id, { max_youtube_imports: val });
      setEditingYtLimit(false);
    } catch {} finally {
      setSavingYtLimit(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Info cards */}
      <div className="lg:col-span-2 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <InfoCard icon={<Users size={16} />} label="Users" value={`${tenant.current_user_count}/${tenant.max_users || '∞'}`} />
          <InfoCard icon={<MapPin size={16} />} label={`Trips · ${rangeLabel}`} value={totalTrips} />
          <InfoCard icon={<HardDrive size={16} />} label="Storage" value={`${formatStorage(tenant.storage_used_mb)} / ${formatStorage(tenant.storage_limit_mb)}`} />
          <InfoCard icon={<BarChart3 size={16} />} label="Avg. Active" value={avgUsers} />
        </div>

        {/* YouTube import limit — editable by super admin */}
        <div className="glass-panel p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-surface-500">
              <Youtube size={16} />
              <span className="text-xs">YouTube Import Limit</span>
            </div>
            {editingYtLimit ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={ytLimitValue}
                  onChange={(e) => setYtLimitValue(e.target.value)}
                  min="0"
                  max="500"
                  className="w-20 px-2 py-1 rounded-lg border border-surface-300 bg-surface-50 text-sm text-surface-900 text-right focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <button
                  onClick={saveYtLimit}
                  disabled={savingYtLimit}
                  className="px-3 py-1 bg-brand-600 text-white rounded-lg text-xs font-medium hover:bg-brand-700 disabled:opacity-50 transition-colors"
                >
                  {savingYtLimit ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditingYtLimit(false); setYtLimitValue(String(tenant.max_youtube_imports ?? 20)); }}
                  className="text-xs text-surface-400 hover:text-surface-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditingYtLimit(true)}
                className="text-lg font-bold text-surface-900 hover:text-brand-600 transition-colors cursor-pointer"
                title="Click to edit"
              >
                {tenant.max_youtube_imports ?? 20}
              </button>
            )}
          </div>
        </div>

        <div className="glass-panel p-5">
          <h3 className="text-sm font-semibold text-surface-900 mb-3">Tenant Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
            <Detail label="Slug" value={tenant.slug} />
            <Detail label="Subdomain" value={tenant.subdomain} />
            <Detail label="Custom Domain" value={tenant.custom_domain || '—'} />
            <Detail label="Plan" value={plan?.display_name || '—'} />
            <Detail label="Stripe Customer" value={tenant.stripe_customer_id || 'Not connected'} />
            <Detail label="Created" value={formatDate(tenant.created_at)} />
            <Detail label="Last updated" value={tenant.updated_at ? formatDate(tenant.updated_at) : '—'} />
            <Detail label="Storage" value={`${formatStorage(tenant.storage_used_mb)} / ${formatStorage(tenant.storage_limit_mb)}`} />
          </div>
        </div>

        {/* Latest activity — most recent users to join + most recent
            metric day so admins can see the pulse without leaving the
            Overview tab. */}
        <LatestActivityPanel users={users} metrics={metrics} />

        {/* Recent usage mini chart */}
        {metrics.length > 0 && (
          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-3">Usage · {rangeLabel}</h3>
            <div className="flex items-end gap-1 h-24">
              {metrics.map((m, i) => {
                const max = Math.max(...metrics.map(x => x.active_users), 1);
                const h = (m.active_users / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${m.date}: ${m.active_users} users, ${m.trips_created} trips`}>
                    <div className="w-full rounded-sm bg-brand-500/80 transition-all" style={{ height: `${h}%`, minHeight: '2px' }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-[10px] text-surface-500">{metrics[0]?.date}</span>
              <span className="text-[10px] text-surface-500">{metrics[metrics.length - 1]?.date}</span>
            </div>
          </div>
        )}

      </div>

      {/* Right: Quick Actions */}
      <div className="space-y-4">
        <div className="glass-panel p-5">
          <h3 className="text-sm font-semibold text-surface-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <QuickActionLink icon={<Users size={16} />} label="Manage Users" description={`${tenant.current_user_count} active users`} onClick={() => navigate(`${base}/users`)} />
            <QuickActionLink icon={<CreditCard size={16} />} label="Billing & Plan" description={plan?.display_name || 'Free plan'} onClick={() => navigate(`${base}/billing`)} />
            <QuickActionLink icon={<Palette size={16} />} label="Customise Branding" description="Colours, logos & fonts" onClick={() => navigate(`${base}/branding`)} />
            <QuickActionLink icon={<ToggleLeft size={16} />} label="Feature Flags" description="Toggle features on/off" onClick={() => navigate(`${base}/features`)} />
            <QuickActionLink icon={<BarChart3 size={16} />} label="View Analytics" description="Usage & performance" onClick={() => navigate(`${base}/analytics`)} />
            <QuickActionLink icon={<Settings size={16} />} label="Settings" description="Admin team & config" onClick={() => navigate(`${base}/settings`)} />
            <QuickActionLink
              icon={<RefreshCw size={16} className={provisioning ? 'animate-spin' : ''} />}
              label="Re-provision B2C"
              description={provisioning ? 'Provisioning...' : 'Re-seed B2C plans & features'}
              onClick={handleReprovision}
            />
            {provisionResult && (
              <div className={`text-xs px-3 py-2 rounded-lg mt-1 ${provisionResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {provisionResult.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Plan history — full timeline of every Platform-tier plan stay
          for this tenant. Useful for billing audits, churn analysis, and
          enforcing the one-shot Starter-plan rule. */}
      <TenantPlanHistory tenantId={tenant.id} tenantName={tenant.name ?? 'tenant'} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* WINDOW SELECTOR — preset chips + custom date range                         */
/* ═══════════════════════════════════════════════════════════════════════════ */

function WindowSelector({
  value, onChange, rangeLabel, loading,
}: {
  value: WindowSel;
  onChange: (next: WindowSel) => void;
  rangeLabel: string;
  loading?: boolean;
}) {
  const presets: Preset[] = [7, 14, 30, 90];
  const [customOpen, setCustomOpen] = useState(value.kind === 'custom');
  // Default custom inputs to whatever resolves from the current state,
  // so toggling Custom doesn't blank the dates out.
  const resolved = resolveWindow(value);
  const [from, setFrom] = useState<string>(value.kind === 'custom' ? value.from : resolved.from);
  const [to,   setTo]   = useState<string>(value.kind === 'custom' ? value.to   : resolved.to);

  const applyCustom = () => {
    if (!from || !to) return;
    onChange({ kind: 'custom', from, to });
  };

  return (
    <div className="glass-panel p-3 flex flex-wrap items-center gap-2">
      <Calendar size={14} className="text-surface-500" />
      <span className="text-xs uppercase tracking-wider text-surface-500 mr-1">Window</span>
      {presets.map(p => {
        const active = value.kind === 'preset' && value.days === p;
        return (
          <button
            key={p}
            onClick={() => { onChange({ kind: 'preset', days: p }); setCustomOpen(false); }}
            className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
              active
                ? 'bg-brand-500/10 border-brand-500/40 text-brand-700 dark:text-brand-400'
                : 'bg-surface-0 border-surface-200 text-surface-600 hover:bg-surface-100 hover:text-surface-900'
            }`}
          >
            {p}d
          </button>
        );
      })}
      <button
        onClick={() => setCustomOpen(o => !o)}
        className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
          value.kind === 'custom' || customOpen
            ? 'bg-brand-500/10 border-brand-500/40 text-brand-700 dark:text-brand-400'
            : 'bg-surface-0 border-surface-200 text-surface-600 hover:bg-surface-100 hover:text-surface-900'
        }`}
      >
        Custom
      </button>

      {customOpen && (
        <>
          <input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-surface-0 text-surface-900"
          />
          <span className="text-xs text-surface-500">→</span>
          <input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-surface-0 text-surface-900"
          />
          <button
            onClick={applyCustom}
            disabled={!from || !to}
            className="px-2.5 py-1 rounded-md text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            Apply
          </button>
        </>
      )}

      <div className="ml-auto flex items-center gap-2">
        {loading && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-brand-700 dark:text-brand-400 px-2 py-0.5 rounded-md bg-brand-500/10 border border-brand-500/30">
            <RefreshCw size={11} className="animate-spin" />
            Updating…
          </span>
        )}
        <span className="text-xs text-surface-500">{rangeLabel}</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* LATEST ACTIVITY PANEL — surfaces on the Overview tab                       */
/* ═══════════════════════════════════════════════════════════════════════════ */


function LatestActivityPanel({ users, metrics }: { users: AppTenantUser[]; metrics: UsageMetric[] }) {
  // Most recent five user joins.
  const latestUsers = [...users]
    .filter(u => !!u.joined_at)
    .sort((a, b) => +new Date(b.joined_at!) - +new Date(a.joined_at!))
    .slice(0, 5);

  const latestMetric = metrics.length > 0 ? metrics[metrics.length - 1] : null;

  if (latestUsers.length === 0 && !latestMetric) return null;

  return (
    <div className="glass-panel p-5">
      <h3 className="text-sm font-semibold text-surface-900 mb-3">Latest activity</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Latest day metrics */}
        {latestMetric && (
          <div className="rounded-lg border border-surface-200 bg-surface-50/60 dark:bg-surface-0 p-3">
            <div className="text-[10px] uppercase tracking-wider text-surface-500 mb-1.5">
              {latestMetric.date}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-surface-500">Active</div>
                <div className="font-bold text-surface-900 tabular-nums">{formatCompact(latestMetric.active_users)}</div>
              </div>
              <div>
                <div className="text-surface-500">Trips</div>
                <div className="font-bold text-surface-900 tabular-nums">{latestMetric.trips_created}</div>
              </div>
              <div>
                <div className="text-surface-500">AI calls</div>
                <div className="font-bold text-surface-900 tabular-nums">{formatCompact(latestMetric.ai_requests)}</div>
              </div>
              <div>
                <div className="text-surface-500">Storage</div>
                <div className="font-bold text-surface-900 tabular-nums">{formatStorage(latestMetric.storage_used_mb)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Latest user joins */}
        {latestUsers.length > 0 && (
          <div className="rounded-lg border border-surface-200 bg-surface-50/60 dark:bg-surface-0 p-3">
            <div className="text-[10px] uppercase tracking-wider text-surface-500 mb-1.5">
              Latest user signups
            </div>
            <ul className="space-y-1.5">
              {latestUsers.map(u => (
                <li key={u.user_id} className="flex items-center gap-2 text-xs">
                  <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-600 dark:text-brand-400 flex items-center justify-center text-[10px] font-bold shrink-0">
                    {(guestDisplayName(u) || u.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-surface-900 truncate">{guestDisplayName(u)}</div>
                    <div className="text-[10px] text-surface-500 truncate">
                      {u.email && u.email !== u.full_name ? u.email + ' · ' : ''}
                      {u.joined_at ? formatDate(u.joined_at) : ''}
                    </div>
                  </div>
                  <span className="text-[10px] text-surface-400 capitalize shrink-0">{u.role}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* ADMINS TAB                                                                 */
/*                                                                            */
/* One row per tenant_admins entry, joined with auth.users.last_sign_in_at    */
/* via the get_tenant_admin_signin_activity RPC. Surfaces whether each        */
/* admin has actually used their account, the last time they did, and a       */
/* coarse status chip (active / stale / inactive / never_signed_in / pending) */
/* so the super-admin can spot ghost invites + churned admins at a glance.    */
/* ═══════════════════════════════════════════════════════════════════════════ */

function AdminsTab({ tenantId }: { tenantId: string }) {
  const [rows, setRows]       = useState<TenantAdminSigninActivity[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTenantAdminSigninActivity(tenantId)
      .then(data => { if (!cancelled) setRows(data); })
      .catch(err  => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); })
      .finally(()  => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId]);

  if (loading) return (
    <div className="glass-panel p-6 flex items-center gap-3">
      <Spinner size="sm" />
      <span className="text-sm text-surface-500">Loading admin sign-in activity…</span>
    </div>
  );

  if (error) return (
    <div className="glass-panel p-6 text-sm text-red-600 flex items-start gap-2">
      <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
      <div>
        <div className="font-medium">Could not load admin activity</div>
        <div className="text-xs text-red-500 mt-1 font-mono break-all">{error}</div>
      </div>
    </div>
  );

  if (!rows || rows.length === 0) return (
    <div className="glass-panel p-6 text-sm text-surface-500">No admins on this tenant yet.</div>
  );

  return (
    <div className="glass-panel p-6">
      <div className="flex items-center gap-2 mb-4">
        <UserCheck size={18} className="text-brand-600" />
        <h3 className="text-sm font-semibold text-surface-900">Tenant Admins & Sign-in Activity</h3>
      </div>
      <p className="text-xs text-surface-500 mb-4">
        Shows everyone on this tenant's <code className="font-mono">tenant_admins</code> roster. Status is computed from
        Supabase's <code className="font-mono">auth.users.last_sign_in_at</code> — refreshed on every Platform sign-in.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-surface-500 uppercase tracking-wider border-b border-overlay/[0.06]">
            <tr>
              <th className="text-left py-2 pr-4 font-medium">Email</th>
              <th className="text-left py-2 pr-4 font-medium">Role</th>
              <th className="text-left py-2 pr-4 font-medium">Invited</th>
              <th className="text-left py-2 pr-4 font-medium">Accepted</th>
              <th className="text-left py-2 pr-4 font-medium">Last sign-in</th>
              <th className="text-left py-2 pr-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-overlay/[0.04]">
            {rows.map(r => (
              <tr key={r.tenant_admin_id} className="text-surface-900">
                <td className="py-3 pr-4">
                  <span className="font-medium">{r.email || '—'}</span>
                </td>
                <td className="py-3 pr-4 capitalize text-surface-700">{r.role}</td>
                <td className="py-3 pr-4 text-surface-600">{formatDate(r.invited_at)}</td>
                <td className="py-3 pr-4 text-surface-600">
                  {r.accepted_at ? formatDate(r.accepted_at) : <span className="text-amber-600">—</span>}
                </td>
                <td className="py-3 pr-4 text-surface-600">
                  {r.last_sign_in_at ? formatRelativeFromNow(r.last_sign_in_at) : <span className="text-surface-400">never</span>}
                </td>
                <td className="py-3 pr-4"><AdminStatusChip status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminStatusChip({ status }: { status: TenantAdminSigninActivity['status'] }) {
  // Five statuses map to four colour bands. The labels are deliberately
  // short — this lives in a table cell that can't grow. Hover reveals
  // the longer description via the title attribute.
  const meta: Record<TenantAdminSigninActivity['status'], { label: string; cls: string; title: string }> = {
    pending:         { label: 'Pending',   cls: 'bg-amber-100  text-amber-800',  title: 'Invite sent but never accepted. Resend the invite if the email got lost.' },
    never_signed_in: { label: 'No login',  cls: 'bg-amber-100  text-amber-800',  title: 'Accepted the invite but has not signed in yet.' },
    active:          { label: 'Active',    cls: 'bg-emerald-100 text-emerald-800', title: 'Signed in within the last 7 days.' },
    stale:           { label: 'Stale',     cls: 'bg-surface-200 text-surface-700', title: 'Last sign-in was 7–30 days ago.' },
    inactive:        { label: 'Inactive',  cls: 'bg-surface-200 text-surface-500', title: 'Last sign-in was more than 30 days ago.' },
  };
  const m = meta[status];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${m.cls}`}
      title={m.title}
    >
      {m.label}
    </span>
  );
}

/** "3 days ago", "2 hours ago", "just now". Compact, since it lives in a
 *  narrow table column. Falls back to formatDate for anything > 30 days. */
function formatRelativeFromNow(iso: string): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '—';
  const deltaMs = Date.now() - ts;
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1)    return 'just now';
  if (minutes < 60)   return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)     return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30)      return `${days}d ago`;
  // 30+ days: fall back to absolute date — relative feels meaningless at that scale.
  return formatDate(iso);
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* BRANDING TAB                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function BrandingTab({ branding }: { branding?: BrandingConfig }) {
  if (!branding) return <div className="text-surface-500 py-8">No branding config found.</div>;
  return (
    <div className="glass-panel p-6 space-y-4 max-w-2xl">
      <h3 className="text-sm font-semibold text-surface-900">Branding Configuration</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <Detail label="App Name" value={branding.app_name} />
        <div className="flex items-center gap-2">
          <span className="text-surface-500">Primary Colour:</span>
          <div className="w-6 h-6 rounded border border-white/10" style={{ backgroundColor: branding.primary_color }} />
          <span className="text-surface-700 font-mono text-xs">{branding.primary_color}</span>
        </div>
        {branding.secondary_color && (
          <div className="flex items-center gap-2">
            <span className="text-surface-500">Secondary:</span>
            <div className="w-6 h-6 rounded border border-white/10" style={{ backgroundColor: branding.secondary_color }} />
            <span className="text-surface-700 font-mono text-xs">{branding.secondary_color}</span>
          </div>
        )}
        <Detail label="Font Family" value={branding.font_family || 'Default (DM Sans)'} />
        <Detail label="Welcome Message" value={branding.welcome_message || '—'} />
        <Detail label="Support Email" value={branding.support_email || '—'} />
        <Detail label="Logo URL" value={branding.logo_url || '—'} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* FEATURES TAB                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function FeaturesTab({ features, tenantId, updateFeatures, plan, planDefaults, isSuperAdmin }: {
  features?: FeatureConfig; tenantId: string;
  updateFeatures: (id: string, input: Record<string, boolean>) => Promise<void>;
  plan?: TenantPlan;
  planDefaults: PlanFeatureDefault[];
  isSuperAdmin: boolean;
}) {
  if (!features) return <div className="text-surface-500 py-8">No feature config found.</div>;

  // Index plan defaults by feature_key so the App column can render
  // "what this tenant's current plan actually grants".
  const planMap: Record<string, PlanFeatureDefault> = {};
  for (const d of planDefaults) planMap[d.feature_key] = d;
  const planLabel = plan?.display_name || plan?.name || 'this plan';

  // ── Group keys by category, separately for Platform + App tiers.
  //    Tier-prefixed collapse keys ("platform:Branding" / "app:Core")
  //    avoid collisions when categories share a name.
  const groupByCategory = (keys: string[]): [string, string[]][] => {
    const map: Record<string, string[]> = {};
    for (const k of keys) {
      const cat = FEATURE_META[k]?.category;
      if (!cat) continue;
      (map[cat] ||= []).push(k);
    }
    for (const cat of Object.keys(map)) {
      map[cat].sort((a, b) => (FEATURE_META[a]?.label || '').localeCompare(FEATURE_META[b]?.label || ''));
    }
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const platformGroups = groupByCategory(PLATFORM_FEATURE_KEYS);
  const appGroups      = groupByCategory(APP_FEATURE_KEYS);

  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleCat = (key: string) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const handleToggle = async (key: string, value: boolean) => {
    if (!isSuperAdmin) return;  // defense-in-depth — write also gated server-side
    try { await updateFeatures(tenantId, { [key]: value }); } catch {}
  };

  return (
    <div className="space-y-4">
      {/* Read-only banner — only shown when the viewer isn't a super
          admin. Route guard already redirects non-super-admins away
          from this page, so this is defense-in-depth in case a
          future role gains access here. */}
      {!isSuperAdmin && (
        <div className="rounded-xl border border-amber-300/50 bg-amber-50/60 dark:bg-amber-950/15 px-4 py-2.5 flex items-center gap-2.5">
          <Lock size={14} className="text-amber-700 dark:text-amber-400 shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            <strong>Read-only.</strong> Only super admins can edit tenant features. Contact your platform team for changes.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* ── Platform Features (B2B — feature_configs columns) ──── */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h3 className="text-sm font-semibold text-surface-900">Platform Features</h3>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-500/10 text-brand-700 dark:text-brand-400">B2B</span>
          {!isSuperAdmin && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-100 dark:bg-surface-200/30 text-surface-600">
              <Lock size={9} /> View only
            </span>
          )}
          <span className="text-[10px] text-surface-500 ml-auto">
            {isSuperAdmin
              ? 'Tenant-level on/off — saved to feature_configs'
              : 'Tenant-level on/off — visible to super admins for edits'}
          </span>
        </div>
        <div className="divide-y divide-overlay/[0.04]">
          {platformGroups.map(([cat, keys]) => {
            const ck = `platform:${cat}`;
            const isCol = collapsed.has(ck);
            const enabled = keys.filter(k => (features as any)[k] === true).length;
            return (
              <div key={ck}>
                <button
                  onClick={() => toggleCat(ck)}
                  className="w-full flex items-center gap-2 py-2 hover:bg-overlay/[0.03] -mx-2 px-2 rounded-md transition-colors"
                >
                  <ChevronDown size={12}
                    className={`text-surface-500 transition-transform ${isCol ? '-rotate-90' : ''}`} />
                  <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wider">{cat}</span>
                  <span className="ml-auto text-[10px] text-surface-500">{enabled} of {keys.length}</span>
                </button>
                {!isCol && (
                  <div className="space-y-0.5 pb-2">
                    {keys.map(key => {
                      const meta = FEATURE_META[key];
                      const en = (features as any)[key] === true;
                      // Skip B2C-only keys (these have plan-defaults but no feature_configs column).
                      if (APP_B2C_ONLY_KEYS.has(key)) return null;
                      return (
                        <Toggle
                          key={key}
                          label={meta.label}
                          description={meta.description}
                          enabled={en}
                          disabled={!isSuperAdmin}
                          onChange={v => handleToggle(key, v)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── App Features (B2C — per-plan in plan_feature_defaults) ──
          Reflects the tenant's CURRENT plan: each row shows whether
          the feature is included on this plan and, when applicable,
          the plan's configured limit. Read-only here — edits live
          in the Plan Builder for the same plan. */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h3 className="text-sm font-semibold text-surface-900">App Features</h3>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-700 dark:text-violet-400">B2C</span>
          <span className="text-[10px] text-surface-500">
            on plan <strong className="text-surface-700">{planLabel}</strong>
          </span>
          <Link
            to="/admin/plan-builder"
            className="ml-auto text-[10px] font-semibold text-brand-700 dark:text-brand-400 hover:underline"
          >
            Edit in Plan Builder →
          </Link>
        </div>
        <div className="divide-y divide-overlay/[0.04]">
          {appGroups.map(([cat, keys]) => {
            const ck = `app:${cat}`;
            const isCol = collapsed.has(ck);
            const includedCount = keys.filter(k => planMap[k]?.is_enabled === true).length;
            return (
              <div key={ck}>
                <button
                  onClick={() => toggleCat(ck)}
                  className="w-full flex items-center gap-2 py-2 hover:bg-overlay/[0.03] -mx-2 px-2 rounded-md transition-colors"
                >
                  <ChevronDown size={12}
                    className={`text-surface-500 transition-transform ${isCol ? '-rotate-90' : ''}`} />
                  <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wider">{cat}</span>
                  <span className="ml-auto text-[10px] text-surface-500">
                    {includedCount} of {keys.length} included
                  </span>
                </button>
                {!isCol && (
                  <ul className="pb-2 space-y-1.5">
                    {keys.map(key => {
                      const meta    = FEATURE_META[key];
                      const def     = planMap[key];
                      const enabled = def?.is_enabled === true;
                      const limit   = def?.limit_value;
                      let badgeText: string;
                      let badgeTone: string;
                      if (!enabled) {
                        badgeText = 'Not included';
                        badgeTone = 'bg-surface-100 dark:bg-surface-200/30 text-surface-500';
                      } else if (meta.hasLimit) {
                        badgeText = limit === null || limit === undefined
                          ? 'Unlimited'
                          : String(limit);
                        badgeTone = 'bg-brand-500/10 text-brand-700 dark:text-brand-400';
                      } else {
                        badgeText = 'Included';
                        badgeTone = 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
                      }
                      return (
                        <li
                          key={key}
                          className={`flex items-start gap-3 px-2 py-1.5 rounded-md hover:bg-overlay/[0.02] ${
                            !enabled ? 'opacity-60' : ''
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-surface-800 font-medium">{meta.label}</div>
                            <div className="text-[11px] text-surface-500 truncate">{meta.description}</div>
                          </div>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 tabular-nums ${badgeTone}`}>
                            {badgeText}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* USAGE TAB                                                                  */
/* ═══════════════════════════════════════════════════════════════════════════ */

type FeatureSort = 'most-used' | 'least-used' | 'a-z' | 'last-used';

function UsageTab({ metrics, rangeLabel, planDefaults }: { metrics: UsageMetric[]; rangeLabel: string; rangeDays: number; planDefaults: PlanFeatureDefault[] }) {
  // ── Foldable + sort + show-unused state ──
  // The Feature Usage card is the focal point of this tab on B2B2C
  // tenants where AI usage is high; tenant admins asked to be able
  // to scan "is this plan worth what we're paying?" at a glance,
  // then drill in. So:
  //   • header is always visible with a "X of N used" counter
  //   • body defaults CLOSED (single click to open)
  //   • inside the body, a small toolbar lets them re-sort and
  //     surface unused-but-included-in-plan features
  // State is local to this tab — doesn't need to persist across
  // navigations and a fresh page load defaulting closed matches the
  // "at-a-glance first" intent.
  const [featureUsageOpen, setFeatureUsageOpen] = useState(false);
  const [featureSort, setFeatureSort] = useState<FeatureSort>('most-used');
  const [showUnused, setShowUnused] = useState(false);

  if (metrics.length === 0) return <div className="text-surface-500 py-8">No usage data in the selected range.</div>;

  const total = (key: keyof UsageMetric) => metrics.reduce((s, m) => s + (m[key] as number), 0);

  // Aggregate per-feature usage across the visible window. Same shape
  // the Tenant Dashboard uses for its Feature Usage panel — surfaces
  // newly-gated features (max_flights, max_buddy_trip_chats, etc.)
  // alongside Trip Itineraries.
  const featureAgg = new Map<string, { name: string; category: string; total: number; users: number; lastUsed: string | null }>();
  for (const m of metrics) {
    for (const f of (m.feature_usage_data || [])) {
      const cur = featureAgg.get(f.feature_key) ?? {
        name: f.feature_name, category: f.category, total: 0, users: 0, lastUsed: null,
      };
      cur.total += f.total_usage || 0;
      cur.users  = Math.max(cur.users, f.unique_users || 0);
      if (f.last_used_at && (!cur.lastUsed || f.last_used_at > cur.lastUsed)) cur.lastUsed = f.last_used_at;
      featureAgg.set(f.feature_key, cur);
    }
  }

  // Plan-size denominator: the count of features the tenant's plan
  // ENABLES (is_enabled=true). Compares against the number of those
  // that were actually exercised in the window for the "X of N used"
  // header. Falls back to the count of features-with-usage if the
  // plan defaults haven't loaded yet (avoids "0 of 0").
  const planEnabledKeys = new Set(planDefaults.filter(d => d.is_enabled).map(d => d.feature_key));
  const planSize        = planEnabledKeys.size;
  const usedKeys        = new Set(Array.from(featureAgg.entries()).filter(([, v]) => v.total > 0).map(([k]) => k));
  const usedInPlan      = planSize > 0
    ? Array.from(usedKeys).filter(k => planEnabledKeys.has(k)).length
    : usedKeys.size;

  // Build the actual row set. When `showUnused` is on, append
  // zero-rows for every plan-enabled feature that has no entry in
  // featureAgg — so the user sees the WHOLE plan with usage data
  // overlaid. The merge prefers the aggregated row when both exist.
  //
  // Filter out zero-usage rows that came from plan-DISABLED features.
  // The App-side aggregate emits a row for every feature in its
  // catalogue (LEFT JOIN), which means Advanced Roles / Visual Context
  // etc. show up with total=0 even when the plan doesn't ship them.
  // They're not "unused" — they're unavailable. Used (total>0) rows
  // are always preserved regardless of plan membership so historical
  // usage from a downgraded feature still surfaces.
  const baseRows = Array.from(featureAgg.entries())
    .map(([key, info]) => ({ key, ...info }))
    .filter(r => r.total > 0 || planEnabledKeys.size === 0 || planEnabledKeys.has(r.key));
  let featureRows = baseRows;
  if (showUnused) {
    const present = new Set(baseRows.map(r => r.key));
    const zeroRows: typeof baseRows = [];
    for (const d of planDefaults) {
      if (!d.is_enabled) continue;
      if (present.has(d.feature_key)) continue;
      const meta = FEATURE_META[d.feature_key];
      zeroRows.push({
        key:      d.feature_key,
        name:     meta?.label || d.feature_key,
        category: meta?.category || 'other',
        total:    0,
        users:    0,
        lastUsed: null,
      });
    }
    featureRows = [...baseRows, ...zeroRows];
  }

  // Apply user-chosen sort. 'most-used' is the historical default;
  // 'least-used' surfaces neglected paid features; 'a-z' is a
  // stable scan order; 'last-used' surfaces freshly-active features
  // even if total volume is low.
  featureRows = [...featureRows].sort((a, b) => {
    if (featureSort === 'least-used') return a.total - b.total;
    if (featureSort === 'a-z')        return (FEATURE_META[a.key]?.label || a.name).localeCompare(FEATURE_META[b.key]?.label || b.name);
    if (featureSort === 'last-used') {
      // Nulls sink to bottom regardless of direction.
      const aT = a.lastUsed ? Date.parse(a.lastUsed) : 0;
      const bT = b.lastUsed ? Date.parse(b.lastUsed) : 0;
      return bT - aT;
    }
    return b.total - a.total;  // 'most-used' (default)
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InfoCard icon={<Users size={16} />} label="Total Active Users" value={formatCompact(total('active_users'))} />
        <InfoCard icon={<MapPin size={16} />} label="Trips Created" value={total('trips_created')} />
        <InfoCard icon={<BarChart3 size={16} />} label="AI Requests" value={formatCompact(total('ai_requests'))} />
        <InfoCard icon={<HardDrive size={16} />} label="Avg Storage" value={formatStorage(Math.round(total('storage_used_mb') / metrics.length))} />
      </div>

      {/* Per-feature breakdown — picks up max_flights, max_buddy_trip_chats,
          and every other gated count feature alongside Trip Itineraries.
          The container always renders so the header chip is visible even
          when nobody used any features (zero-state "0 of N used" is
          informative — tells the admin "your plan has N features but
          nobody touched them in this window"). */}
      {(featureRows.length > 0 || planSize > 0) && (
        <div className="glass-panel p-5">
          {/* Foldable header — entire row is the click target so the
              chevron + counter + title all act as one button. */}
          <button
            type="button"
            onClick={() => setFeatureUsageOpen(o => !o)}
            className="w-full flex items-center justify-between gap-3 text-left"
            aria-expanded={featureUsageOpen}
          >
            <div className="flex items-center gap-2 min-w-0">
              <ChevronDown
                size={14}
                className={`text-surface-500 shrink-0 transition-transform ${featureUsageOpen ? '' : '-rotate-90'}`}
              />
              <h3 className="text-sm font-semibold text-surface-900 truncate">Feature usage · {rangeLabel}</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold uppercase tracking-wider shrink-0">
                {usedInPlan} of {planSize || baseRows.length} used
              </span>
            </div>
          </button>

          {featureUsageOpen && (
            <>
              {/* Toolbar: sort dropdown + show-unused toggle. Sits between
                  the foldable header and the table so the controls are
                  visible the moment the panel opens. */}
              <div className="flex items-center gap-2 mt-3 mb-3 flex-wrap">
                <label className="flex items-center gap-1.5 text-xs text-surface-600">
                  <span className="font-semibold uppercase tracking-wider text-[10px]">Sort</span>
                  <select
                    value={featureSort}
                    onChange={(e) => setFeatureSort(e.target.value as FeatureSort)}
                    className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-white text-surface-900"
                  >
                    <option value="most-used">Most used</option>
                    <option value="least-used">Least used</option>
                    <option value="a-z">A → Z</option>
                    <option value="last-used">Last used</option>
                  </select>
                </label>
                {planSize > 0 && (
                  <label className="flex items-center gap-1.5 text-xs text-surface-600 cursor-pointer select-none ml-auto">
                    <input
                      type="checkbox"
                      checked={showUnused}
                      onChange={(e) => setShowUnused(e.target.checked)}
                      className="w-3.5 h-3.5"
                    />
                    Show unused plan features
                  </label>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-200 text-[10px] uppercase tracking-wider text-surface-500">
                      <th className="text-left py-2 px-2 font-semibold">Feature</th>
                      <th className="text-left py-2 px-2 font-semibold">Category</th>
                      <th className="text-right py-2 px-2 font-semibold">Total uses</th>
                      <th className="text-right py-2 px-2 font-semibold">Unique users</th>
                      <th className="text-left py-2 px-2 font-semibold">Last used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 px-2 text-center text-xs text-surface-500">
                          No usage in this window. Toggle "Show unused plan features" to see what the plan ships with.
                        </td>
                      </tr>
                    )}
                    {featureRows.map(r => {
                      const meta = FEATURE_META[r.key];
                      const label = meta?.label || r.name || r.key;
                      // Zero-rows (only present when showUnused=true) are
                      // tinted muted so the user's eye picks out the
                      // exercised features first.
                      const dim = r.total === 0;
                      return (
                        <tr key={r.key} className={`border-b border-surface-100 hover:bg-surface-50/50 ${dim ? 'opacity-60' : ''}`}>
                          <td className="py-2 px-2 font-medium text-surface-900">{label}</td>
                          <td className="py-2 px-2 text-surface-500 capitalize">{r.category}</td>
                          <td className="py-2 px-2 text-right font-bold text-surface-900 tabular-nums">{r.total.toLocaleString()}</td>
                          <td className="py-2 px-2 text-right text-surface-700 tabular-nums">{r.users.toLocaleString()}</td>
                          <td className="py-2 px-2 text-surface-500 text-xs">{r.lastUsed ? formatDate(r.lastUsed) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell">Date</th>
                <th className="table-header-cell">Active Users</th>
                <th className="table-header-cell">Trips</th>
                <th className="table-header-cell">AI Requests</th>
                <th className="table-header-cell">Storage</th>
              </tr>
            </thead>
            <tbody>
              {[...metrics].reverse().map(m => (
                <tr key={m.id} className="table-row">
                  <td className="table-cell text-surface-900">{m.date}</td>
                  <td className="table-cell">{m.active_users}</td>
                  <td className="table-cell">{m.trips_created}</td>
                  <td className="table-cell">{m.ai_requests}</td>
                  <td className="table-cell">{formatStorage(m.storage_used_mb)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* USERS TAB (App DB users via edge function)                                 */
/* ═══════════════════════════════════════════════════════════════════════════ */

function UsersTab({ tenantId, users, loading }: { tenantId: string; users: AppTenantUser[]; loading: boolean }) {
  const { user: platformUser } = useAuth();
  // Expanded user_id → drives the inline plan-history drawer below the row.
  // Only one row at a time keeps the table compact and avoids parallel
  // PROVISION_SECRET fetches for every user.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // The user we're about to impersonate. null = no modal open.
  // Setting this opens the confirm modal; clearing it closes it.
  const [impersonateTarget, setImpersonateTarget] = useState<AppTenantUser | null>(null);
  const [impersonateReason, setImpersonateReason] = useState('');
  // Tracks WHICH button was clicked (or null when idle), so only that
  // button shows the spinner — not both. Disabling logic uses
  // `impersonateRunningMode !== null` instead of a separate flag.
  const [impersonateRunningMode, setImpersonateRunningMode] = useState<'direct' | 'preview' | null>(null);
  const impersonateInFlight = impersonateRunningMode !== null;

  // ── Tenant Admin cross-reference ──────────────────────────────────
  // Users tab shows tenant_users (App DB end travelers). Admins tab
  // shows tenant_admins (Platform DB people who can manage the tenant).
  // The two rosters overlap in practice — an agency owner is usually
  // both. Without a cross-reference, the operator has to flip tabs
  // to know which Users are also Admins. We fetch the admin roster
  // once and flag matching rows with an inline chip.
  //
  // We match on email (case-insensitive) rather than user_id because
  // the two databases have independent auth.users — same email,
  // different uuid in each project.
  const [adminEmails, setAdminEmails] = useState<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    fetchTenantAdminSigninActivity(tenantId)
      .then(rows => {
        if (cancelled) return;
        setAdminEmails(new Set(
          (rows ?? [])
            .map(r => (r.email || '').trim().toLowerCase())
            .filter(Boolean),
        ));
      })
      .catch(err => {
        // Non-fatal — the table still renders, just without the
        // admin chip. Surface to console for debug.
        console.warn('[UsersTab] failed to load admin roster for chip:', err);
        if (!cancelled) setAdminEmails(new Set());
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  // ── Per-user Template right ───────────────────────────────────────
  // Mirror users[] locally so toggling the Template right reflects
  // instantly without a full re-fetch of the (slow) tenant-users edge
  // fn. The parent owns the canonical list; we keep our copy in sync
  // when the parent updates, but bump it ourselves on toggle.
  const [localUsers, setLocalUsers] = useState<AppTenantUser[]>(users);
  useEffect(() => { setLocalUsers(users); }, [users]);
  // user_id currently in flight on a right toggle. Drives the spinner +
  // disables further clicks on the same row.
  const [rightBusyId, setRightBusyId] = useState<string | null>(null);

  async function toggleTemplateRight(u: AppTenantUser) {
    const current = !!(u.user_rights?.itinerary_template);
    const next    = !current;
    setRightBusyId(u.user_id);
    try {
      const r = await setUserRight({
        tenantId,
        userId:    u.user_id,
        rightKey:  'itinerary_template',
        value:     next,
        actorId:    platformUser?.id,
        actorEmail: platformUser?.email ?? undefined,
      });
      // Splice the updated row into our local list. We use the
      // authoritative user_rights echoed back by the edge fn rather
      // than just `next` — covers the case where some other admin
      // changed a different key on the same row concurrently.
      setLocalUsers(prev => prev.map(row =>
        row.user_id === u.user_id
          ? { ...row, user_rights: r.user_rights }
          : row,
      ));
      toast.success(next
        ? `Template tab unlocked for ${u.email || u.user_id}`
        : `Template tab revoked for ${u.email || u.user_id}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update right');
    } finally {
      setRightBusyId(null);
    }
  }

  // ── Promote/Demote creator tier ──────────────────────────────────
  // Manual override on profiles.creator_tier. The pg_cron auto-promote
  // job covers the organic path (50 sales OR 500 followers); this is
  // for pre-promoting known influencers or demoting bad actors.
  //
  // Reuses rightBusyId for the per-row spinner — only one mutation can
  // be in flight per row at a time anyway (the buttons all sit in the
  // same Actions cell), so a single busy id is enough.
  async function toggleCreatorTier(u: AppTenantUser) {
    const currentTier = (u.creator_tier ?? 'starter') as 'featured' | 'starter';
    const nextTier    = currentTier === 'featured' ? 'starter' : 'featured';
    setRightBusyId(u.user_id);
    try {
      const r = await setCreatorTier({
        tenantId,
        userId:     u.user_id,
        tier:       nextTier,
        actorId:    platformUser?.id,
        actorEmail: platformUser?.email ?? undefined,
      });
      setLocalUsers(prev => prev.map(row =>
        row.user_id === u.user_id
          ? { ...row, creator_tier: r.creator_tier, creator_tier_promoted_at: r.creator_tier_promoted_at }
          : row,
      ));
      if (r.unchanged) {
        // Idempotent path — server returned without writing. Skip the
        // celebratory toast to avoid misleading the admin into thinking
        // a change happened when nothing did.
        toast(`${u.email || u.user_id} is already ${nextTier}.`);
      } else if (nextTier === 'featured') {
        toast.success(`Promoted ${u.email || u.user_id} to Featured Creator`);
      } else {
        toast.success(`Demoted ${u.email || u.user_id} to Starter`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update tier');
    } finally {
      setRightBusyId(null);
    }
  }

  /** Fire start-impersonation in either mode, then open the redirect
   *  URL in a new tab.
   *
   *  Modes:
   *    - 'direct'  — land on /home with the impersonated session active.
   *                  Fastest path; matches Stripe-style "Sign in as".
   *    - 'preview' — land on the tenant's anonymous splash with the
   *                  session STAGED (not active). The operator sees the
   *                  unauth splash, then clicks a floating CTA on the
   *                  splash to activate the session and enter the app.
   *                  Useful for debugging splash → auth bugs that only
   *                  reproduce in the unauth flow (auto-redirect issues,
   *                  Google sign-in button state, slow first-paint, OG
   *                  image mismatches). */
  async function doImpersonate(mode: 'direct' | 'preview') {
    if (!impersonateTarget) return;
    setImpersonateRunningMode(mode);
    const r = await startImpersonation({
      tenantId,
      userId: impersonateTarget.user_id,
      reason: impersonateReason.trim() || undefined,
      mode,
    });
    setImpersonateRunningMode(null);
    if (!r.ok || !r.redirect_url) {
      toast.error(r.error || 'Failed to start impersonation');
      return;
    }
    toast.success(
      mode === 'preview'
        ? `Opening unauth splash for ${r.target_email || impersonateTarget.email}`
        : `Opening session as ${r.target_email || impersonateTarget.email}`,
      {
        description: mode === 'preview'
          ? 'New tab will land on the splash. Click the yellow bar at the top to enter the app.'
          : 'A new tab will open. Use the red banner there to Exit.',
      },
    );
    // Open in a new tab. window.open with _blank is widely permitted
    // because it's directly tied to the Confirm button click (user
    // gesture). If the popup is blocked, fall back to copying the URL
    // to clipboard so the operator can paste it.
    const win = window.open(r.redirect_url, '_blank', 'noopener,noreferrer');
    if (!win) {
      try {
        await navigator.clipboard.writeText(r.redirect_url);
        toast.message('Popup blocked — link copied to clipboard. Paste into a new tab.');
      } catch {
        toast.error('Popup blocked. Re-issue impersonation and allow popups.');
      }
    }
    setImpersonateTarget(null);
    setImpersonateReason('');
  }

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner size="md" /></div>;
  }

  if (users.length === 0) {
    return (
      <div className="glass-panel p-8 text-center">
        <Users size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-surface-500">No users found for this tenant.</p>
        <p className="text-xs text-surface-400 mt-1">Users appear here when they sign in to the tenant&apos;s app.</p>
      </div>
    );
  }

  const planColors: Record<string, string> = {
    free: 'bg-surface-200 text-surface-700',
    explorer: 'bg-blue-100 text-blue-400',
    adventurer: 'bg-teal-100 text-teal-700',
    premium: 'bg-amber-100 text-amber-400',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-surface-900">{localUsers.length} User{localUsers.length !== 1 ? 's' : ''}</h3>
        <span className="text-[11px] text-surface-500">Click a row to view plan history</span>
      </div>

      {/* ── Confirm modal for Sign in as user ─────────────────────────
          Lives inline (not a separate component) because the state
          (target user, reason, in-flight) only matters here. Click
          outside to dismiss; Cancel keeps state but closes; Confirm
          fires the impersonation and opens the redirect in a new tab. */}
      {impersonateTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => !impersonateInFlight && setImpersonateTarget(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-overlay/[0.08] bg-surface-0 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-overlay/[0.06] flex items-center gap-2">
              <ShieldAlert size={16} className="text-red-600" />
              <h3 className="text-base font-semibold text-surface-900">Sign in as user?</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-surface-700">
                You will sign in as{' '}
                <span className="font-mono font-semibold text-surface-900">{impersonateTarget.email}</span>.
                Actions you take will appear AS this user in the App database. A red banner will be visible at the top of every page until you Exit.
              </p>
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[12px] text-amber-700 flex items-start gap-2">
                <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
                <span>This will be logged in both <code>platform_audit_log</code> and the App's <code>admin_audit_log</code>. The session expires in ~1 hour.</span>
              </div>
              <label className="block text-[12px] font-medium text-surface-700">
                Reason (optional, captured in audit)
                <textarea
                  value={impersonateReason}
                  onChange={e => setImpersonateReason(e.target.value)}
                  placeholder="e.g. Debugging missing trip — ticket #1234"
                  rows={2}
                  maxLength={500}
                  className="mt-1 w-full px-3 py-2 rounded-lg text-sm bg-surface-50 border border-overlay/[0.06] focus:outline-none focus:ring-2 focus:ring-red-400/40"
                />
              </label>
              {/* Two confirm paths so the operator can pick where to
                  land. Direct (red) goes straight to /home as the user.
                  Preview (amber) goes to the tenant's unauth splash so
                  splash-only bugs are visible. Both are equally fast on
                  the auth side — the only difference is the landing
                  page. */}
              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <button
                  onClick={() => setImpersonateTarget(null)}
                  disabled={impersonateInFlight}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => doImpersonate('preview')}
                  disabled={impersonateInFlight}
                  title="Land on the tenant's anonymous splash. Click the yellow bar there to enter."
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500 text-amber-950 hover:bg-amber-600 disabled:opacity-60"
                >
                  {impersonateRunningMode === 'preview' && <Loader2 size={14} className="animate-spin" />}
                  Open at splash (unauth preview)
                </button>
                <button
                  onClick={() => doImpersonate('direct')}
                  disabled={impersonateInFlight}
                  title="Skip the splash. Land directly inside the app as this user."
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {impersonateRunningMode === 'direct' && <Loader2 size={14} className="animate-spin" />}
                  Open signed in (skip splash)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell w-6"></th>
                <th className="table-header-cell">User</th>
                <th className="table-header-cell">Role</th>
                <th className="table-header-cell">Plan</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Joined</th>
                <th className="table-header-cell">Last activity</th>
                {/* Template right — controls the Template tab on the App
                    ResultsScreen (the "Earn on every copy" creator-
                    monetization surface). Per-user grant stored in
                    tenant_users.user_rights.itinerary_template. */}
                <th
                  className="table-header-cell"
                  title="Unlocks the Template tab on the App's itinerary view (earn on every copy)."
                >
                  Template
                </th>
                {/* Creator Program tier. Manual Promote/Demote button
                    sits in the cell. Auto-promote pg_cron handles the
                    organic 50-sales / 500-followers path; this column
                    is for influencer pre-promotion + policy demotion. */}
                <th
                  className="table-header-cell"
                  title="Featured Creator tier — boosted placement in public feeds + exclusive poster styles. Auto-promotes at 50 sales or 500 followers."
                >
                  Creator
                </th>
                <th className="table-header-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {localUsers.map(u => {
                const isTenantAdmin = !!u.email && adminEmails.has(u.email.trim().toLowerCase());
                const templateGranted = !!(u.user_rights?.itinerary_template);
                const rightInFlight = rightBusyId === u.user_id;
                const isFeatured = (u.creator_tier ?? 'starter') === 'featured';
                const sales      = Number(u.confirmed_blueprint_sales) || 0;
                const followers  = Number(u.follower_count) || 0;
                const isExpanded = expandedId === u.user_id;
                const toggle = () => setExpandedId(isExpanded ? null : u.user_id);
                return (
                  <Fragment key={u.user_id}>
                    <tr
                      className="table-row cursor-pointer hover:bg-surface-50/60 dark:hover:bg-surface-100/30"
                      onClick={toggle}
                    >
                      <td className="table-cell">
                        <ChevronDown
                          size={14}
                          className={`text-surface-400 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                        />
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-bold shrink-0">
                            {(guestDisplayName(u) || u.email).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-surface-900">{guestDisplayName(u)}</div>
                            <div className="text-xs text-surface-500 font-mono" title={u.user_id}>
                              {u.email || (u.is_anonymous ? shortId(u.user_id) : '')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <span className="capitalize text-sm text-surface-700">{u.role}</span>
                          {/* Tenant Admin chip — surfaces when this end-user's
                              email is ALSO on the tenant_admins roster
                              (Platform DB). The two databases have independent
                              auth.users, so we match on email. */}
                          {isTenantAdmin && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                              title="Also on tenant_admins — can sign in to Platform to manage this tenant."
                            >
                              <Shield size={9} /> Tenant Admin
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${planColors[u.plan_key] || planColors.free}`}>
                          {u.plan_name}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`text-xs font-medium ${u.subscription_status === 'active' ? 'text-emerald-400' : u.subscription_status === 'trialing' ? 'text-amber-400' : 'text-surface-400'}`}>
                          {u.subscription_status === 'active' ? 'Active' : u.subscription_status === 'trialing' ? 'Trial' : 'No sub'}
                        </span>
                      </td>
                      <td className="table-cell text-sm text-surface-500">
                        {u.joined_at ? formatDate(u.joined_at) : '—'}
                      </td>
                      <td className="table-cell text-sm">
                        {u.last_active_at ? (
                          <div title={`Sign-in: ${u.last_sign_in_at ?? '—'}\nLast trip: ${u.last_trip_at ?? '—'}\nLast chat: ${u.last_chat_at ?? '—'}`}>
                            <div className="text-surface-700 tabular-nums">{formatRelativeTime(u.last_active_at)}</div>
                            <div className="text-[10px] text-surface-400 tabular-nums">{formatDate(u.last_active_at)}</div>
                          </div>
                        ) : (
                          <span className="text-surface-400">Never active</span>
                        )}
                      </td>
                      <td className="table-cell">
                        {/* Template right toggle. Click without expanding the
                            row. Stamps tenant_users.user_rights.itinerary_template
                            in the App DB via set-user-right edge fn. The App's
                            ResultsScreen reads this on session boot and shows
                            the Template tab only when granted. */}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            if (rightInFlight) return;
                            toggleTemplateRight(u);
                          }}
                          disabled={rightInFlight}
                          title={templateGranted
                            ? 'Template tab unlocked. Click to revoke.'
                            : 'Template tab hidden. Click to grant.'}
                          className={[
                            'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors',
                            templateGranted
                              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25'
                              : 'bg-surface-100 text-surface-500 hover:bg-surface-200 dark:bg-surface-100/30 dark:text-surface-400 dark:hover:bg-surface-100/40',
                            rightInFlight ? 'opacity-60 cursor-wait' : '',
                          ].join(' ')}
                        >
                          {rightInFlight
                            ? <Loader2 size={11} className="animate-spin" />
                            : <LayoutTemplate size={11} />}
                          {templateGranted ? 'Granted' : 'Off'}
                        </button>
                      </td>
                      <td className="table-cell">
                        {/* Creator-tier button. Featured renders as an
                            emerald "Featured" chip with a crown; Starter
                            renders as a muted "Promote" CTA. The
                            progress hint underneath ("12 / 50 sales · 3
                            / 500 followers") gives the admin context
                            for whether organic auto-promote is close.
                            Click stops row-expand propagation. */}
                        <div className="flex flex-col items-start gap-0.5">
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              if (rightInFlight) return;
                              toggleCreatorTier(u);
                            }}
                            disabled={rightInFlight}
                            title={isFeatured
                              ? 'Featured Creator — boosted in feeds + exclusive templates. Click to demote.'
                              : 'Promote to Featured Creator (boost in public feeds + exclusive poster styles). Click to grant.'}
                            className={[
                              'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold transition-colors',
                              isFeatured
                                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-500/25'
                                : 'bg-surface-100 text-surface-500 hover:bg-surface-200 dark:bg-surface-100/30 dark:text-surface-400 dark:hover:bg-surface-100/40',
                              rightInFlight ? 'opacity-60 cursor-wait' : '',
                            ].join(' ')}
                          >
                            {rightInFlight
                              ? <Loader2 size={11} className="animate-spin" />
                              : isFeatured ? <Crown size={11} /> : <Sparkles size={11} />}
                            {isFeatured ? 'Featured' : 'Promote'}
                          </button>
                          {/* Sub-line: organic-promote progress. Only show
                              for Starters so Featured rows stay tidy. */}
                          {!isFeatured && (sales > 0 || followers > 0) && (
                            <span className="text-[9px] text-surface-400 tabular-nums">
                              {sales}/50 · {followers}/500
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell text-right">
                        {/* Sign in as user. Stop row-click propagation
                            so this doesn't accidentally expand the
                            plan-history drawer. Disabled for users
                            without an email — magic links can't be
                            minted without one (anonymous accounts). */}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            setImpersonateTarget(u);
                            setImpersonateReason('');
                          }}
                          disabled={!u.email}
                          title={u.email
                            ? 'Sign in as this user (opens new tab with red impersonation banner)'
                            : 'Cannot impersonate — user has no email (anonymous account)'}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-red-700 dark:text-red-300 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <KeyRound size={11} />
                          Sign in as
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-surface-200 bg-surface-50/40 dark:bg-surface-100/20">
                        <td colSpan={10} className="p-0">
                          <UserPlanHistory
                            tenantId={tenantId}
                            userId={u.user_id}
                            userLabel={u.email || u.full_name || u.user_id}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* HELPERS                                                                    */
/* ═══════════════════════════════════════════════════════════════════════════ */

function InfoCard({ icon, label, value }: { icon: ReactNode; label: string; value: string | number }) {
  return (
    <div className="glass-panel p-4">
      <div className="flex items-center gap-2 mb-1 text-surface-500">{icon}<span className="text-xs">{label}</span></div>
      <div className="text-lg font-bold text-surface-900">{value}</div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-surface-500">{label}: </span>
      <span className="text-surface-700">{value}</span>
    </div>
  );
}

/** "5m ago" / "3h ago" / "2d ago" / fallback to ISO date for older. */
function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  if (days < 30)  return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function QuickActionLink({ icon, label, description, onClick }: {
  icon: ReactNode; label: string; description: string; onClick: () => void;
}) {
  return (
    <button onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-xl border border-surface-200 bg-surface-0 hover:bg-surface-50 hover:border-surface-300 transition-all text-left group">
      <div className="p-2 rounded-lg badge-brand group-hover:bg-brand-500/20 transition-colors shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-surface-900">{label}</div>
        <div className="text-xs text-surface-500 truncate">{description}</div>
      </div>
      <ChevronRight size={14} className="text-surface-300 group-hover:text-surface-500 transition-colors shrink-0" />
    </button>
  );
}

